// Help Center content. Each article has a title and body paragraphs; strings
// starting with a digit and a full stop render as numbered steps.
import React from 'react';
import { Users, Grid, Bell, Settings, Shield, Smartphone } from 'lucide-react';

export const helpCategories = [
  {
    id: 'getting-started',
    icon: <Users className="h-6 w-6 text-blue-600" />,
    title: 'Getting Started',
    description: 'Set up your account and your first portal',
    articles: [
      {
        title: 'Creating your first account',
        body: [
          'Signing up takes about a minute and needs no card details.',
          '1. Go to taptile.app and choose Start for Free.',
          '2. Enter your name, work email address and organisation name.',
          '3. Check your inbox for the invitation email and click the link to set your password.',
          '4. Sign in and you will land on your Admin Dashboard with a starter portal ready to edit.',
          'If the email has not arrived within a few minutes, check your spam folder, then contact us and we will resend it.',
        ],
      },
      {
        title: 'Setting up your organisation',
        body: [
          'Everything your users see is controlled from the Design tab of your Admin Dashboard.',
          '1. Open the Design tab and set your organisation name and header text.',
          '2. Upload a logo or header banner image.',
          '3. Pick your primary colour, background colour and tile colours under Colours & Fonts.',
          '4. Press Save Changes. Your portal updates instantly for everyone.',
          'Use the Preview button at any time to see exactly what your users will see, on both web and the native app.',
        ],
      },
      {
        title: 'Inviting users',
        body: [
          'Users need an account before they can sign in to your portal.',
          '1. Open the Users tab and choose Add User.',
          '2. Enter their name and email address, and optionally assign them to one or more groups.',
          '3. Save. They receive an email invitation with a link to set their own password.',
          'Invitations do not expire, so users can accept whenever suits them. You can resend or delete an invitation at any point from the same tab.',
        ],
      },
      {
        title: 'Installing the app on a phone',
        body: [
          'Your portal works in any browser, but installing it puts an icon on the home screen and enables push notifications.',
          'On iPhone or iPad: open the portal in Safari, tap the Share button, then Add to Home Screen.',
          'On Android: open the portal in Chrome, tap the three-dot menu, then Install app.',
          'Schools and organisations using our native iOS app can instead download Taptile from the App Store and sign in with their short code.',
          'Tip: the Share PWA button in your Admin Dashboard generates a link and QR code you can send to users so they can do this in one tap.',
        ],
      },
    ],
  },
  {
    id: 'managing-tiles',
    icon: <Grid className="h-6 w-6 text-green-600" />,
    title: 'Managing Tiles',
    description: 'Create, style and organise your tiles',
    articles: [
      {
        title: 'Creating a new tile',
        body: [
          'Tiles are the buttons on your portal. Each one opens a link, dials a number or embeds a tool.',
          '1. Open the Tiles tab and press Add.',
          '2. Give the tile a short title — one or two words works best on small screens.',
          '3. Choose the type: Link opens a website, Phone dials a number, Chat embeds a chat widget.',
          '4. Paste the destination URL or phone number, then pick an icon from the library, an emoji, or upload your own image.',
          '5. Save. The tile appears immediately in your portal and in the app.',
        ],
      },
      {
        title: 'Customising tile appearance',
        body: [
          'There are two levels of control: a global style for all tiles, and overrides on individual tiles.',
          'Global: in the Design tab, choose a Tile Style — Soft (a pale tint), Solid (filled with colour), Gradient or Outlined. You can also set the tile shape and the default tile background and text colours.',
          'Per tile: click a tile to open it, then set its own Background or Text colour. A tile with its own colour ignores the global setting.',
          'To put a tile back under global control, open it and press Use theme colour next to that picker. When a tile is inheriting, the editor shows "Following theme colour".',
          'One difference to be aware of: the native app does not support Gradient, so gradient tiles appear solid there. Switch the preview to Native app to see exactly how the phone app will render your settings.',
        ],
      },
      {
        title: 'Controlling who sees each tile',
        body: [
          'Tiles can be shown to everyone or restricted to particular groups.',
          '1. Create your groups first, in the Users area.',
          '2. Open a tile and select one or more groups under visibility.',
          '3. Save. Only members of those groups will see that tile.',
          'A tile with no groups selected is visible to every signed-in user. This is how you give, for example, Year 6 parents a tile that nobody else sees.',
        ],
      },
      {
        title: 'Reordering tiles and changing the grid',
        body: [
          'Drag the handle on any tile to move it. The new order saves automatically and applies to every user straight away.',
          'To change how many tiles sit on each row, use the column selector (2, 3 or 4) in the Tiles tab.',
          'Note that the native app always shows three tiles per row regardless of this setting, so the column choice affects the web portal only.',
        ],
      },
    ],
  },
  {
    id: 'user-management',
    icon: <Users className="h-6 w-6 text-purple-600" />,
    title: 'User Management',
    description: 'Add users, build groups and set roles',
    articles: [
      {
        title: 'Adding users manually',
        body: [
          'Best for small numbers or for adding someone who joins later.',
          '1. Users tab, then Add User.',
          '2. Fill in their name and email address and choose any groups.',
          '3. Save to send their invitation.',
          'You can also suspend a user instead of deleting them. A suspended user keeps their account and groups but cannot sign in until you reactivate them.',
        ],
      },
      {
        title: 'Importing users from a CSV file',
        body: [
          'The fastest way to add a large list.',
          '1. Prepare a CSV file with a header row containing name and email columns.',
          '2. In the Users tab choose the CSV upload option and select your file.',
          '3. Review the summary — it reports how many users were created and flags any rows it could not read.',
          'Duplicate email addresses are skipped rather than creating a second account, so it is safe to re-upload an updated list.',
          'Schools using Wonde can skip CSV files entirely and sync pupils and staff directly — see the Connect tab.',
        ],
      },
      {
        title: 'Creating user groups',
        body: [
          'Groups are how you target tiles and notifications at part of your audience rather than everyone.',
          '1. Open the Users area and create a group — for example Staff, Year 6 Parents, or Governors.',
          '2. Assign users to it, either when you create them or by editing them later.',
          '3. Use the group when setting tile visibility or when sending a notification.',
          'Groups synced from Wonde are marked as such and are kept up to date automatically; manually created groups are yours to manage.',
        ],
      },
      {
        title: 'Admins and roles',
        body: [
          'There are two kinds of account: standard users, who see the portal, and admins, who can also change it.',
          'To add an admin, open the Admins area and invite them by email. They get the same Admin Dashboard you do.',
          'Only invite admins you are happy to let edit tiles, branding and users, and send notifications to everybody.',
          'Removing an admin takes effect immediately — they lose access the moment you delete them.',
        ],
      },
    ],
  },
  {
    id: 'notifications',
    icon: <Bell className="h-6 w-6 text-orange-600" />,
    title: 'Notifications',
    description: 'Reach users on their phones',
    articles: [
      {
        title: 'Enabling push notifications',
        body: [
          'Push notifications need two things: the user must have installed the app, and they must have allowed notifications when asked.',
          'On iPhone this means adding the portal to the home screen via Safari (or installing our native app) — Apple does not allow push from a normal Safari tab.',
          'On Android, installing from Chrome is enough.',
          'If a user is not receiving notifications, the usual cause is that they are still using a browser tab rather than the installed app.',
        ],
      },
      {
        title: 'Sending a notification now',
        body: [
          '1. Open the Notify tab.',
          '2. Write a short title and your message. Keep the title under about 40 characters so it is not truncated on a lock screen.',
          '3. Choose who receives it — everybody, specific groups, or named individuals.',
          '4. Send. Delivery usually takes a few seconds.',
          'Every notification is also listed in the portal, so users who miss the alert can still read it later.',
        ],
      },
      {
        title: 'Scheduling a notification for later',
        body: [
          'Useful for reminders you want to write now and send at a sensible hour.',
          '1. Compose your notification as usual in the Notify tab.',
          '2. Instead of sending immediately, set a date and time.',
          '3. Save. It shows as Scheduled until it goes out.',
          'You can also set a notification to repeat daily, weekly or monthly, with an optional end date — handy for a weekly newsletter reminder.',
        ],
      },
      {
        title: 'Targeting specific groups',
        body: [
          'Sending everything to everyone is the quickest way to get people to turn notifications off.',
          'When composing, select one or more groups instead of all users. Only members of those groups are notified.',
          'If you select both groups and individuals, everyone named receives it once — there are no duplicate alerts.',
        ],
      },
    ],
  },
  {
    id: 'customization',
    icon: <Settings className="h-6 w-6 text-red-600" />,
    title: 'Branding & Customisation',
    description: 'Make the portal look like yours',
    articles: [
      {
        title: 'Changing colours and branding',
        body: [
          'Open the Design tab and work down the Colours & Fonts section.',
          'Primary colour drives buttons and accents. Background colour sets the page behind your tiles. Tile background and text colours set the default look of every tile that has not been given its own colour.',
          'Header background and header text colours control the banner at the top.',
          'Press Save Changes when you are happy. Changes are live immediately — there is nothing to publish.',
        ],
      },
      {
        title: 'Uploading your logo and header image',
        body: [
          'In the Design tab you can upload both a logo and a wider header banner.',
          'Use a PNG with a transparent background where possible. Wide, short images work best for the banner — roughly three times as wide as they are tall.',
          'Images you upload are kept in your image library, so you can reuse them on tiles later without uploading again.',
          'One thing to know: when a header image is set, the native app shows the image on its own and hides the header text, so put any wording you need inside the image itself.',
        ],
      },
      {
        title: 'Choosing a tile style',
        body: [
          'The Tile Style control in the Design tab changes every tile at once.',
          'Soft gives a pale tint of your tile colour and coloured text — clean and quiet. Solid fills each tile with its colour. Outlined keeps tiles white with a coloured border. Gradient fades the colour, on the web portal only.',
          'Combine this with Tile Shape (default, rounded, square or circle) to get the look you want.',
          'Use the preview and switch between Web and Native app to check both before you save.',
        ],
      },
      {
        title: 'Sharing your portal link',
        body: [
          'Every organisation gets its own portal address plus a short code for the app.',
          'Use the Share PWA button in your Admin Dashboard to get a shareable link and a QR code.',
          'Print the QR code on a newsletter or put the link in an email signature — users scan or click, then add it to their home screen.',
          'Users of our native iOS app can simply enter your short code instead of a long address.',
        ],
      },
    ],
  },
  {
    id: 'security',
    icon: <Shield className="h-6 w-6 text-indigo-600" />,
    title: 'Security & Access',
    description: 'Who can get in, and how',
    articles: [
      {
        title: 'How sign-in works',
        body: [
          'Users sign in with their email address and a password they choose themselves from the invitation email. Passwords are stored hashed and are never visible to you or to us.',
          'If someone forgets their password they can reset it from the sign-in screen.',
          'As an admin you can suspend an account instantly, which blocks sign-in while keeping their history and group membership intact.',
        ],
      },
      {
        title: 'Single sign-on from NexusCMS',
        body: [
          'If your organisation uses NexusCMS, Taptile can be embedded directly inside it and users are signed in automatically — no second password.',
          'Nexus requests a short-lived token from Taptile, opens your dashboard inside its own page, and the session is exchanged behind the scenes.',
          'Setup is handled for you when your account is created from Nexus. In that case the Connect tab shows "Connected to NexusCMS — configured automatically" and there are no keys to copy.',
          'Wider single sign-on, such as Microsoft accounts, is on our roadmap.',
        ],
      },
      {
        title: 'Controlling what is public',
        body: [
          'Tiles and notifications are only ever shown to signed-in users, and only according to their groups.',
          'Your portal address itself is not secret — treat it like a website address — so never put confidential information in a tile title.',
          'For anything sensitive, restrict the tile to a group and rely on the destination system to authenticate the user as well.',
        ],
      },
      {
        title: 'API keys and integrations',
        body: [
          'Integrations such as NexusCMS use an API key tied to your organisation, managed in the Connect tab.',
          'Generate a key when you need one, and revoke it the moment it is no longer in use — revoking takes effect immediately.',
          'Generating a new key invalidates the previous one, so update whatever was using it at the same time.',
          'Treat a key like a password: never paste it into an email or a public document.',
        ],
      },
    ],
  },
  {
    id: 'ios-app',
    icon: <Smartphone className="h-6 w-6 text-slate-700" />,
    title: 'The iOS App',
    description: 'Using the native Taptile app',
    articles: [
      {
        title: 'Signing in with a short code',
        body: [
          'Download Taptile from the App Store, then enter your organisation short code on the first screen.',
          'Your short code is shown in the Admin Dashboard and is usually a shortened version of your organisation name.',
          'Once a code has been entered, the app remembers it — users only sign in with their email and password after that.',
        ],
      },
      {
        title: 'Why the app looks slightly different',
        body: [
          'The app is built natively rather than rendered in a browser, so a few web-only touches do not carry across.',
          'Gradient tiles appear solid, tiles are always three per row, custom web fonts fall back to the system font, and social links always sit at the bottom.',
          'When a header image is set, the app shows the image alone and hides the header text.',
          'To see exactly how your settings will land, open the Preview in your Admin Dashboard and switch to Native app — it lists these differences for your current setup.',
        ],
      },
      {
        title: 'Getting app updates',
        body: [
          'Most improvements reach the app silently without an App Store update.',
          'If something looks out of date, close the app fully by swiping it away in the app switcher, then reopen it — that applies any update already downloaded.',
          'Occasionally a change needs a full App Store release, in which case update Taptile from the App Store as normal.',
        ],
      },
      {
        title: 'Notifications are not arriving',
        body: [
          'Work through these in order.',
          '1. Check the user allowed notifications when the app first asked. If they declined, enable Taptile under Settings, then Notifications on the device.',
          '2. Confirm they are signed in — notifications are targeted by user and group, so a signed-out device receives nothing.',
          '3. Check the notification was sent to a group they belong to.',
          '4. If they are using the portal in a Safari tab rather than the installed app or App Store app, push will not work on iPhone at all.',
        ],
      },
    ],
  },
];
